import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronRight,
  Building2,
  ExternalLink,
  SortAsc,
  SortDesc,
  HardDrive,
  Cog,
  Folder,
  FolderOpen,
  Store,
  Ellipsis,

} from 'lucide-react';
import { searchCustomers, CustomerSearchDto } from '@/features/Customers/customerAPI';
import { SitesSearchDto } from '@/app/Sites/siteApi';
import { DatalayerAssetDto, EnumVideoProcessor } from '@/types/datalayer-asset';
import { AddressDto } from '@/types/common-types';
import { axiosClientAdmin } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import CustomerComponent from './CustomerComponent';
import { Loading } from '@/components/ui/loading';
import Site from '@/app/Sites/Site';
import { useAuthStore } from '@/store/authStore';
import { AddSite } from '@/app/Sites/components/AddSite';
import AddDevice from '@/features/Devices/AddDevice/AddDevice';
import { useSearchParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DevicePage from '../Devices/DevicePage/DevicePage';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useVirtualizer } from '@tanstack/react-virtual';

interface HierarchyCustomer {
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: string;
  archived: boolean;
  email?: string;
  phoneNumber?: string;
  address?: AddressDto;
  sites: HierarchySite[];
}

interface HierarchySite extends SitesSearchDto {
  devices: HierarchyDevice[];
}

interface HierarchyDevice extends DatalayerAssetDto {
  siteName: string;
  siteId: string;
}

interface SiteQueryResponse {
  rows: SitesSearchDto[];
  totalRows: number;
}

interface SiteAssetSearchResponse {
  rows: DatalayerAssetDto[];
  totalRows: number;
}

// Types for view state management
type ViewType = 'empty' | 'customer' | 'site' | 'device' | 'addDevice';

interface ViewState {
  type: ViewType;
  customerId?: string;
  siteId?: string;
  deviceId?: string;
}

// Types for virtualized list items
type VirtualListItemType = 'customer' | 'site' | 'device' | 'loading' | 'empty';

interface VirtualListItem {
  type: VirtualListItemType;
  id: string;
  level: number; // 0 = customer, 1 = site, 2 = device
  customer?: HierarchyCustomer;
  site?: HierarchySite;
  device?: HierarchyDevice;
  customerId?: string;
  siteId?: string;
  isLoading?: boolean;
  message?: string;
}

export default function Viewer2() {
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [customers, setCustomers] = useState<HierarchyCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const authStore = useAuthStore();

  // Lazy loading state for device counts
  const [deviceCounts, setDeviceCounts] = useState<Map<string, { siteCount: number; deviceCount: number }>>(new Map());
  const [loadingCounts, setLoadingCounts] = useState<Set<string>>(new Set());
  
  // Lazy loading state for sites
  const [sitesLoaded, setSitesLoaded] = useState<Set<string>>(new Set());
  const [loadingSites, setLoadingSites] = useState<Set<string>>(new Set());
  
  // Lazy loading state for devices
  const [devicesLoaded, setDevicesLoaded] = useState<Set<string>>(new Set());
  const [loadingDevices, setLoadingDevices] = useState<Set<string>>(new Set());

  // View state management
  const [viewState, setViewState] = useState<ViewState>({ type: 'empty' });
  const [addSiteDialog, setAddSiteDialog] = useState<{
    open: boolean;
    customerId: string;
    customerName: string;
  }>({ open: false, customerId: '', customerName: '' });

  // Sorting state
  const [sortBy, setSortBy] = useState<'name' | 'sites' | 'devices'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // State for tracking if we've attempted URL restoration
  const [urlRestorationAttempted, setUrlRestorationAttempted] = useState(false);

  // Fetch customer data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch customers
      const customersResponse = await searchCustomers({
        pageNumber: 1,
        pageSize: 100, // Get a reasonable number of customers
        includeArchived: false,
      });

      if (customersResponse.rows) {
        // Only fetch customers initially, sites will be loaded on-demand
        const customersWithoutSites = customersResponse.rows.map((customer: CustomerSearchDto) => ({
          id: customer.id,
          name: customer.name,
          hasContracts: customer.hasContracts,
          accountNumber: customer.accountNumber,
          archived: customer.archived,
          email: customer.email,
          phoneNumber: customer.phoneNumber,
          address: customer.address,
          sites: [], // Will be populated on-demand when customer is expanded
        }));

        setCustomers(customersWithoutSites);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to fetch device counts for a specific customer
  const fetchDeviceCounts = useCallback(async (customerId: string) => {
    // Don't fetch if already loading or already have data
    if (loadingCounts.has(customerId) || deviceCounts.has(customerId)) {
      return;
    }

    setLoadingCounts(prev => new Set([...prev, customerId]));

    try {
      const customer = customers.find(c => c.id === customerId);
      if (!customer) return;

      let totalDeviceCount = 0;
      const siteCount = customer.sites.length;

      // Fetch device counts for each site
      await Promise.all(
        customer.sites.map(async (site) => {
          try {
            const devicesResponse = await axiosClientAdmin.get<SiteAssetSearchResponse>(
              `/Customer/${customerId}/project/${site.id}/assets`
            );
            totalDeviceCount += devicesResponse.data.rows?.length || 0;
          } catch {
            // Ignore errors for individual sites
          }
        })
      );

      setDeviceCounts(prev => new Map([...prev, [customerId, { siteCount, deviceCount: totalDeviceCount }]]));
    } catch {
      // Handle error silently
    } finally {
      setLoadingCounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(customerId);
        return newSet;
      });
    }
  }, [customers, deviceCounts, loadingCounts]);

  // Function to fetch sites for a specific customer
  const fetchSitesForCustomer = useCallback(async (customerId: string) => {
    // Don't fetch if already loading or already loaded
    if (loadingSites.has(customerId) || sitesLoaded.has(customerId)) {
      return;
    }

    setLoadingSites(prev => new Set([...prev, customerId]));

    try {
      const sitesResponse = await axiosClientAdmin.get<SiteQueryResponse>(
        `/Customer/${customerId}/project/search`,
        {
          params: {
            PageNumber: 1,
            PageSize: 100,
          },
        }
      );

      const sitesWithEmptyDevices = (sitesResponse.data.rows || []).map((site: SitesSearchDto) => ({
        ...site,
        devices: [], // Devices will still be loaded on-demand
      }));

      // Update the customer with their sites
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? { ...customer, sites: sitesWithEmptyDevices }
            : customer
        )
      );

      // Mark this customer's sites as loaded
      setSitesLoaded(prev => new Set([...prev, customerId]));
    } catch {
      // Handle error silently - customer will show no sites
    } finally {
      setLoadingSites(prev => {
        const newSet = new Set(prev);
        newSet.delete(customerId);
        return newSet;
      });
    }
  }, [loadingSites, sitesLoaded]);

  // Function to fetch devices for a specific site
  const fetchDevicesForSite = useCallback(async (customerId: string, siteId: string) => {
    const siteKey = `${customerId}-${siteId}`;
    
    // Don't fetch if already loading or already loaded
    if (loadingDevices.has(siteKey) || devicesLoaded.has(siteKey)) {
      return;
    }

    setLoadingDevices(prev => new Set([...prev, siteKey]));

    try {
      const devicesResponse = await axiosClientAdmin.get<SiteAssetSearchResponse>(
        `/Customer/${customerId}/project/${siteId}/assets`
      );

      const devicesWithSiteInfo: HierarchyDevice[] = (
        devicesResponse.data.rows || []
      ).map((device: DatalayerAssetDto) => ({
        ...device,
        siteName: '', // Will be filled from site data
        siteId: siteId,
      }));

      // Update the specific site with its devices
      setCustomers(prevCustomers =>
        prevCustomers.map(customer =>
          customer.id === customerId
            ? {
                ...customer,
                sites: customer.sites.map(site =>
                  site.id === siteId
                    ? { ...site, devices: devicesWithSiteInfo }
                    : site
                )
              }
            : customer
        )
      );

      // Mark this site's devices as loaded
      setDevicesLoaded(prev => new Set([...prev, siteKey]));
    } catch {
      // Handle error silently - site will show no devices
    } finally {
      setLoadingDevices(prev => {
        const newSet = new Set(prev);
        newSet.delete(siteKey);
        return newSet;
      });
    }
  }, [loadingDevices, devicesLoaded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch devices for customer when viewing customer page
  useEffect(() => {
    if (viewState.type === 'customer' && viewState.customerId && customers.length > 0) {
      const customerId = viewState.customerId;
      const customer = customers.find(c => c.id === customerId);
      if (customer?.sites) {
        // Fetch devices for all sites when viewing customer
        customer.sites.forEach(site => {
          fetchDevicesForSite(customerId, site.id);
        });
      }
    }
  }, [viewState, customers, fetchDevicesForSite]);

  // Update specific customer in the list without full reload
  const updateCustomerInList = useCallback((updatedCustomer: Partial<HierarchyCustomer> | Partial<{ id: string; name: string; hasContracts: boolean; accountNumber: string; archived: boolean; email?: string; phoneNumber?: string; sites: SitesSearchDto[]; }>) => {
    setCustomers((prevCustomers) =>
      prevCustomers.map((customer) => {
        if (customer.id === updatedCustomer.id) {
          // Cast the updated customer to HierarchyCustomer type for compatibility
          const updated = { ...customer, ...updatedCustomer } as HierarchyCustomer;
          return updated;
        }
        return customer;
      })
    );
  }, []);

  // Targeted site addition function - avoids full page reload
  const addSiteToCustomer = useCallback(
    (customerId: string, newSite: { id: string; name: string }) => {
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer.id === customerId
            ? {
                ...customer,
                sites: [
                  ...customer.sites,
                  {
                    id: newSite.id,
                    name: newSite.name,
                    companyId: customerId,
                    companyName: customer.name,
                    devices: [], // New sites start with no devices
                  },
                ],
              }
            : customer
        )
      );

      // Automatically expand the customer to show the new site
      setExpandedCustomers((prev) => new Set([...prev, customerId]));
    },
    []
  );

  // Targeted site update function - avoids full page reload
  const updateSiteInList = useCallback(
    (customerId: string, updatedSite: Partial<{ id: string; name: string }>) => {
      setCustomers((prevCustomers) =>
        prevCustomers.map((customer) =>
          customer.id === customerId
            ? {
                ...customer,
                sites: customer.sites.map((site) =>
                  site.id === updatedSite.id ? { ...site, ...updatedSite } : site
                ),
              }
            : customer
        )
      );
    },
    []
  );

  // Targeted device update function - avoids full page reload
  // const updateDeviceInList = useCallback(
  //   (customerId: string, siteId: string, updatedDevice: Partial<{ id: string; name: string }>) => {
  //     setCustomers((prevCustomers) =>
  //       prevCustomers.map((customer) =>
  //         customer.id === customerId
  //           ? {
  //               ...customer,
  //               sites: customer.sites.map((site) =>
  //                 site.id === siteId
  //                   ? {
  //                       ...site,
  //                       devices: site.devices.map((device) =>
  //                         device.id === updatedDevice.id || device.externalId === updatedDevice.id
  //                           ? { ...device, ...updatedDevice }
  //                           : device
  //                       ),
  //                     }
  //                   : site
  //               ),
  //             }
  //           : customer
  //       )
  //     );
  //   },
  //   []
  // );

  // Filter customers, sites, and devices based on search term
  const filteredCustomers = useCallback(() => {
    const filtered = customers.filter((customer) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();

      // Check if customer name matches
      const customerMatches = customer.name.toLowerCase().includes(searchLower);

      // Check if any site or device matches
      const hasMatchingSiteOrDevice = customer.sites.some((site) => {
        const siteMatches = site.name.toLowerCase().includes(searchLower);
        const hasMatchingDevice = site.devices.some((device) =>
          device.name.toLowerCase().includes(searchLower)
        );
        return siteMatches || hasMatchingDevice;
      });

      return customerMatches || hasMatchingSiteOrDevice;
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      if (sortBy === 'name') {
        if (sortOrder === 'asc') {
          return a.name.localeCompare(b.name);
        } else {
          return b.name.localeCompare(a.name);
        }
      } else if (sortBy === 'sites') {
        if (sortOrder === 'asc') {
          return a.sites.length - b.sites.length;
        } else {
          return b.sites.length - a.sites.length;
        }
      } else if (sortBy === 'devices') {
        const aDeviceCount = a.sites.reduce((acc, site) => acc + site.devices.length, 0);
        const bDeviceCount = b.sites.reduce((acc, site) => acc + site.devices.length, 0);
        if (sortOrder === 'asc') {
          return aDeviceCount - bDeviceCount;
        } else {
          return bDeviceCount - aDeviceCount;
        }
      }
      return 0;
    });
  }, [customers, searchTerm, sortBy, sortOrder]);

  // Filter sites within each customer
  const getFilteredSites = useCallback(
    (customer: HierarchyCustomer) => {
      if (!searchTerm) return customer.sites;

      const searchLower = searchTerm.toLowerCase();

      // If customer name matches, show all sites
      const customerMatches = customer.name.toLowerCase().includes(searchLower);
      if (customerMatches) {
        return customer.sites;
      }

      // Otherwise, filter sites by name or devices
      return customer.sites.filter((site) => {
        const siteMatches = site.name.toLowerCase().includes(searchLower);
        const hasMatchingDevice = site.devices.some((device) =>
          device.name.toLowerCase().includes(searchLower)
        );
        return siteMatches || hasMatchingDevice;
      });
    },
    [searchTerm]
  );

  // Filter devices within each site
  const getFilteredDevices = useCallback(
    (site: HierarchySite, customerMatches: boolean = false) => {
      if (!searchTerm) return site.devices;

      const searchLower = searchTerm.toLowerCase();

      // If site name matches or customer name matches, show all devices
      const siteMatches = site.name.toLowerCase().includes(searchLower);
      if (siteMatches || customerMatches) {
        return site.devices;
      }

      // Otherwise, filter devices by name
      return site.devices.filter((device) => device.name.toLowerCase().includes(searchLower));
    },
    [searchTerm]
  );

  // Create flattened virtual list items from hierarchical data
  const virtualListItems = useMemo((): VirtualListItem[] => {
    const items: VirtualListItem[] = [];
    const filtered = filteredCustomers();

    filtered.forEach((customer) => {
      // Add customer item
      items.push({
        type: 'customer',
        id: `customer-${customer.id}`,
        level: 0,
        customer,
        customerId: customer.id,
      });

      // If customer is expanded, add sites
      if (expandedCustomers.has(customer.id)) {
        const isLoadingSites = loadingSites.has(customer.id);
        const sitesAreLoaded = sitesLoaded.has(customer.id);
        
        if (isLoadingSites) {
          items.push({
            type: 'loading',
            id: `loading-sites-${customer.id}`,
            level: 1,
            customerId: customer.id,
            isLoading: true,
            message: 'Loading sites...',
          });
        } else if (sitesAreLoaded) {
          const filteredSites = getFilteredSites(customer);
          
          if (filteredSites.length === 0) {
            items.push({
              type: 'empty',
              id: `empty-sites-${customer.id}`,
              level: 1,
              customerId: customer.id,
              message: 'No sites found',
            });
          } else {
            filteredSites.forEach((site) => {
              // Add site item
              items.push({
                type: 'site',
                id: `site-${site.id}`,
                level: 1,
                site,
                customer,
                customerId: customer.id,
                siteId: site.id,
              });

              // If site is expanded, add devices
              if (expandedSites.has(site.id)) {
                const siteKey = `${customer.id}-${site.id}`;
                const isLoadingDevices = loadingDevices.has(siteKey);
                const devicesAreLoaded = devicesLoaded.has(siteKey);
                
                if (isLoadingDevices) {
                  items.push({
                    type: 'loading',
                    id: `loading-devices-${siteKey}`,
                    level: 2,
                    customerId: customer.id,
                    siteId: site.id,
                    isLoading: true,
                    message: 'Loading devices...',
                  });
                } else if (devicesAreLoaded) {
                  const customerMatches = customer.name.toLowerCase().includes(searchTerm.toLowerCase());
                  const filteredDevices = getFilteredDevices(site, customerMatches);
                  
                  if (filteredDevices.length === 0) {
                    items.push({
                      type: 'empty',
                      id: `empty-devices-${siteKey}`,
                      level: 2,
                      customerId: customer.id,
                      siteId: site.id,
                      message: 'No devices found',
                    });
                  } else {
                    filteredDevices.forEach((device) => {
                      items.push({
                        type: 'device',
                        id: `device-${device.id}`,
                        level: 2,
                        device,
                        site,
                        customer,
                        customerId: customer.id,
                        siteId: site.id,
                      });
                    });
                  }
                }
              }
            });
          }
        }
      }
    });

    return items;
  }, [
    filteredCustomers,
    expandedCustomers,
    expandedSites,
    loadingSites,
    sitesLoaded,
    loadingDevices,
    devicesLoaded,
    getFilteredSites,
    getFilteredDevices,
    searchTerm,
  ]);

  // Ref for the scrollable container
  const parentRef = useRef<HTMLDivElement>(null);

  // Setup virtualizer with dynamic sizing
  const virtualizer = useVirtualizer({
    count: virtualListItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const item = virtualListItems[index];
      // Base estimates - these will be adjusted dynamically
      switch (item.type) {
        case 'customer':
          return 32; // Slightly larger estimate to account for potential wrapping
        case 'site':
          return 32; // Slightly larger estimate to account for potential wrapping
        case 'device':
          return 32; // Slightly larger estimate to account for potential wrapping
        case 'loading':
        case 'empty':
          return 36; // Loading/empty state height
        default:
          return 40;
      }
    },
    // Enable dynamic sizing to measure actual rendered heights
    measureElement: (element) => {
      return element?.getBoundingClientRect().height ?? 0;
    },
    overscan: 5, // Render 5 extra items above and below the visible area
  });

  // Auto-expand logic when searching
  useEffect(() => {
    if (searchTerm) {
      // Auto-expand customers that have matches
      const customersToExpand = new Set<string>();
      const sitesToExpand = new Set<string>();
      const searchLower = searchTerm.toLowerCase();

      const filtered = filteredCustomers();
      filtered.forEach((customer) => {
        const customerMatches = customer.name.toLowerCase().includes(searchLower);
        const filteredSites = getFilteredSites(customer);

        // Only expand customer if it directly matches the search term
        if (customerMatches) {
          customersToExpand.add(customer.id);
        }

        // Only expand sites that directly match or have matching devices
        filteredSites.forEach((site) => {
          const siteMatches = site.name.toLowerCase().includes(searchLower);

          // Expand site if its name matches
          if (siteMatches) {
            sitesToExpand.add(site.id);
            // Also ensure parent customer is expanded
            customersToExpand.add(customer.id);
          } else {
            // For device matches, check if any device matches
            const hasMatchingDevice = site.devices.some((device) =>
              device.name.toLowerCase().includes(searchLower)
            );

            if (hasMatchingDevice) {
              sitesToExpand.add(site.id);
              // Also ensure parent customer is expanded
              customersToExpand.add(customer.id);
            }
          }
        });
      });

      setExpandedCustomers(customersToExpand);
      setExpandedSites(sitesToExpand);
    }
  }, [searchTerm, filteredCustomers, getFilteredSites]);

  const toggleCustomer = useCallback((customerId: string) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
      // Fetch sites when expanding a customer for the first time
      fetchSitesForCustomer(customerId);
    }
    setExpandedCustomers(newExpanded);
  }, [expandedCustomers, fetchSitesForCustomer]);

  const toggleSite = useCallback((siteId: string, customerId: string) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
      // Fetch devices when expanding a site for the first time
      fetchDevicesForSite(customerId, siteId);
    }
    setExpandedSites(newExpanded);
  }, [expandedSites, fetchDevicesForSite]);

  // Initialize view state from URL parameters on component mount
  useEffect(() => {
    if (loading || customers.length === 0) {
      return;
    }

    setUrlRestorationAttempted(true);

    const customerId = searchParams.get('customerId');
    const siteId = searchParams.get('siteId');
    const deviceId = searchParams.get('deviceId');

    if (deviceId && siteId && customerId) {
      // Verify all entities exist before setting view state
      const customer = customers.find((c) => c.id === customerId);
      const site = customer?.sites.find((s) => s.id === siteId);
      const device = site?.devices.find(
        (d) =>
          d.id === deviceId ||
          d.externalId === deviceId ||
          d.videoProcessorConfigs?.find((x) => x?.nvrId)?.nvrId === deviceId
      );

      if (customer && site && device) {
        setViewState({
          type: 'device',
          customerId,
          siteId,
          deviceId,
        });
        return;
      }
    } else if (siteId && customerId) {
      // Verify the customer and site exist before setting view state
      const customer = customers.find((c) => c.id === customerId);
      const site = customer?.sites.find((s) => s.id === siteId);

      if (customer && site) {
        setViewState({ type: 'site', customerId, siteId });
        return;
      }
    } else if (customerId) {
      // Verify the customer exists before setting view state
      const customer = customers.find((c) => c.id === customerId);

      if (customer) {
        setViewState({ type: 'customer', customerId });
        return;
      }
    }

    // Only set empty state if there are no URL parameters AND current state is not addDevice
    if (!customerId && !siteId && !deviceId) {
      setViewState((currentState) => {
        // Don't override addDevice state when there are no URL params
        if (currentState.type === 'addDevice') {
          return currentState;
        }
        return { type: 'empty' };
      });
    } else {
      // Data is loaded but entities don't exist - clear invalid URL params and set empty state
      setViewState({ type: 'empty' });
    }
  }, [searchParams, customers, loading]);

  // Update URL parameters when view state changes (but not during initial URL restoration)
  useEffect(() => {
    // Don't update URL if we haven't attempted restoration yet or if we're in the process of restoring
    if (!urlRestorationAttempted || loading) {
      return;
    }

    const params = new URLSearchParams();
    if (viewState.type === 'customer' && viewState.customerId) {
      params.set('customerId', viewState.customerId);
    } else if (viewState.type === 'site' && viewState.customerId && viewState.siteId) {
      params.set('customerId', viewState.customerId);
      params.set('siteId', viewState.siteId);
    } else if (
      viewState.type === 'device' &&
      viewState.customerId &&
      viewState.siteId &&
      viewState.deviceId
    ) {
      params.set('customerId', viewState.customerId);
      params.set('siteId', viewState.siteId);
      params.set('deviceId', viewState.deviceId);
    }
    setSearchParams(params);
  }, [viewState, setSearchParams, urlRestorationAttempted, loading]);

  // Render function for virtual list items
  const renderVirtualItem = useCallback((virtualItem: { index: number; size: number; start: number; key: React.Key }) => {
    const item = virtualListItems[virtualItem.index];
    if (!item) return null;

    const style = {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      textClamp: 1,
      transform: `translateY(${virtualItem.start}px)`,
    };

    switch (item.type) {
      case 'customer': {
        const customer = item.customer!;
        return (
          <div key={item.id} style={style} data-index={virtualItem.index}>
            <div className="relative">
              {/* <ContextMenu>
                <ContextMenuTrigger> */}
                  {/* Selected indicator bar */}
                  {viewState.type === 'customer' && viewState.customerId === customer.id && (
                    <div className="absolute left-0 top-0 h-full w-0.5 bg-lime-400" />
                  )}
                  <button
                    onClick={() => {
                      setViewState({ type: 'customer', customerId: customer.id });
                      toggleCustomer(customer.id);
                    }}
                    onMouseEnter={() => {
                      fetchDeviceCounts(customer.id);
                    }}
                    className="group flex w-full items-center gap-2 text-sm text-white/70 transition-colors hover:bg-[#2E3835] min-h-8"
                  >
                    <ChevronRight
                      className={`h-4 w-4 text-lime transition-transform ${
                        expandedCustomers.has(customer.id) ? 'rotate-90' : ''
                      }`}
                    />
                    {viewState.type === 'customer' && viewState.customerId === customer.id ? (
                      <FolderOpen className="h-4 w-4 text-white" />
                    ) : (
                      <Folder className="h-4 w-4" />
                    )}
                    <span
                      className={`flex-1 line-clamp-1 text-left ${viewState.type === 'customer' && viewState.customerId === customer.id ? 'font-bold text-white' : ''}`}
                    >
                      {customer.name}
                    </span>
                    <div className="flex items-center gap-0">
                      {(() => {
                        const counts = deviceCounts.get(customer.id);
                        const isLoading = loadingCounts.has(customer.id);
                        
                        if (isLoading) {
                          return (
                            <p className="absolute right-0 flex items-center gap-1 rounded-lg bg-black/20 px-2 py-1 text-center text-xs text-gray-400 group-hover:hidden">
                              <div className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-transparent" />
                              Loading...
                            </p>
                          );
                        }
                        
                        if (counts) {
                          return (
                            <>
                              {counts.deviceCount > 0 && (
                                <p className="absolute right-10 flex items-center gap-1 rounded-lg bg-black/20 px-2 py-1 text-center text-xs text-gray-400 group-hover:hidden">
                                  <HardDrive className="h-3 w-3 text-gray-600" />{' '}
                                  {counts.deviceCount}
                                </p>
                              )}
                              {counts.siteCount > 0 && (
                                <p className="absolute right-0 flex items-center gap-1 rounded-lg bg-black/20 px-2 py-1 text-center text-xs text-gray-400 group-hover:hidden">
                                  <Building2 className="h-3 w-3 text-gray-600" />{' '}
                                  {counts.siteCount}
                                </p>
                              )}
                            </>
                          );
                        }
                        
                        if (sitesLoaded.has(customer.id) && customer.sites.length > 0) {
                          return (
                            <p className="absolute right-0 flex items-center gap-1 rounded-lg bg-black/20 px-2 py-1 text-center text-xs text-gray-400 group-hover:hidden">
                              <Building2 className="h-3 w-3 text-gray-600" />{' '}
                              {customer.sites.length}
                            </p>
                          );
                        }
                        
                        return null;
                      })()}

                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          const contextMenuEvent = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: e.currentTarget.getBoundingClientRect().left,
                            clientY: e.currentTarget.getBoundingClientRect().bottom,
                          });
                          e.currentTarget.parentElement?.parentElement?.dispatchEvent(contextMenuEvent);
                        }}
                        className=" items-center gap-0.5 px-2 py-2 font-semibold text-lime opacity-0 transition-opacity hover:bg-lime/10 hover:underline group-hover:opacity-100 hidden"
                      >
                        <Ellipsis className="h-4 w-4"/>
                      </div>
                    </div>
                  </button>
                {/* </ContextMenuTrigger>
                <ContextMenuContent> */}
                  {/* <h2 className="p-2 pb-1 text-xs text-gray-600">Options</h2>
                  <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewState({ type: 'customer', customerId: customer.id });
                    }}
                  >
                    <Cog className="h-3 w-3 text-gray-600" /> Edit Customer
                  </ContextMenuItem> */}
                  {/* <div className="h-[1px] w-full bg-[#2E3835]"></div>
                  <h2 className="p-2 pb-1 text-xs text-gray-600">Add New</h2> */}
                  {/* <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddSiteDialog({
                        open: true,
                        customerId: customer.id,
                        customerName: customer.name,
                      });
                    }}
                  >
                    <Building2 className="h-3 w-3 text-gray-600" />
                    Add Site
                  </ContextMenuItem> */}
                  {/* <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewState({ type: 'addDevice', customerId: customer.id });
                    }}
                  >
                    <HardDrive className="h-3 w-3 text-gray-600" />
                    Add Device
                  </ContextMenuItem> */}
                {/* </ContextMenuContent>
              </ContextMenu> */}
            </div>
          </div>
        );
      }

      case 'site': {
        const site = item.site!;
        const customer = item.customer!;
        return (
          <div key={item.id} style={style} data-index={virtualItem.index}>
            <div className="relative ml-1">
              <ContextMenu>
                <ContextMenuTrigger>
                  {(() => {
                    const shouldShowLimeBar =
                      viewState.type === 'site' &&
                      viewState.siteId === site.id &&
                      viewState.customerId === customer.id;

                    return shouldShowLimeBar ? (
                      <div className="absolute left-0 top-0 h-full w-0.5 bg-lime-400" />
                    ) : null;
                  })()}
                  <button
                    onClick={() => {
                      toggleSite(site.id, customer.id);
                      setViewState({
                        type: 'site',
                        customerId: customer.id,
                        siteId: site.id,
                      });
                    }}
                    className="group flex w-full items-center gap-2 px-2 pr-0 text-sm text-white/70 transition-colors hover:bg-[#2E3835]"
                  >
                    <ChevronRight
                      className={`h-3 w-3 text-lime transition-transform ${
                        expandedSites.has(site.id) ? 'rotate-90' : ''
                      }`}
                    />
                    <Building2 className="h-3 w-3" />
                    <span
                      className={`flex-1 line-clamp-1 text-left ${viewState.type === 'site' && viewState.siteId === site.id && viewState.customerId === customer.id ? 'font-bold text-white' : ''}`}
                    >
                      {site.name}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          const contextMenuEvent = new MouseEvent('contextmenu', {
                            bubbles: true,
                            cancelable: true,
                            clientX: e.currentTarget.getBoundingClientRect().left,
                            clientY: e.currentTarget.getBoundingClientRect().bottom,
                          });
                          e.currentTarget.parentElement?.parentElement?.dispatchEvent(contextMenuEvent);
                        }}
                        className="flex items-center gap-0.5 px-2 py-2 font-semibold text-lime opacity-0 transition-opacity hover:bg-lime/10 hover:underline group-hover:opacity-100"
                      >
                        <Ellipsis className="h-4 w-4"/>
                      </button>
                    </div>
                  </button>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <h2 className="p-2 pb-1 text-xs text-gray-600">Options</h2>
                  <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewState({
                        type: 'site',
                        customerId: customer.id,
                        siteId: site.id,
                      });
                    }}
                  >
                    <Cog className="h-4 w-4 text-gray-600"/>
                    Site Details
                  </ContextMenuItem>
                  <h2 className="p-2 pb-1 text-xs text-gray-600 border-t">Add New</h2>
                  <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewState({ 
                        type: 'addDevice', 
                        customerId: customer.id, 
                        siteId: site.id 
                      });
                    }}
                  >
                    <HardDrive className="h-4 w-4 text-gray-600"/>
                    Add Device
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </div>
          </div>
        );
      }

      case 'device': {
        const device = item.device!;
        const site = item.site!;
        const customer = item.customer!;
        
        const VPC = device.videoProcessorConfigs?.length > 0 ? device.videoProcessorConfigs[0] : null;
        const isV2Device = VPC?.videoProcessor === EnumVideoProcessor.IntegrationHub;
        const nvrId = device.videoProcessorConfigs?.find((x) => x?.nvrId)?.nvrId;

        const isDeviceSelected = () => {
          if (viewState.type !== 'device') return false;
          if (viewState.customerId !== customer.id) return false;
          if (viewState.siteId !== site.id) return false;
          return (
            viewState.deviceId === nvrId ||
            viewState.deviceId === device.id
          );
        };

        return (
          <div key={item.id} style={style} data-index={virtualItem.index}>
            <div className="relative">
              {isDeviceSelected() && (
                <div className="absolute left-0 top-0 h-full w-0.5 bg-lime-400" />
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();

                  if (isV2Device && nvrId) {
                    setViewState({
                      type: 'device',
                      customerId: customer.id,
                      siteId: site.id,
                      deviceId: nvrId,
                    });
                  } else if (device.serialNumber) {
                    const deviceUrl = `https://${authStore.getTenantData()?.themePath.split('/').pop() ?? 'watchful'}.${import.meta.env.VITE_BASE_URL}/devices/${device.serialNumber}`;
                    window.open(deviceUrl, '_blank');
                  } else {
                    const deviceId = device.id;
                    setViewState({
                      type: 'device',
                      customerId: customer.id,
                      siteId: site.id,
                      deviceId: deviceId,
                    });
                  }
                }}
                className="group flex w-full items-center gap-2 px-2 py-1.5 pl-6 text-sm text-white/70 transition-colors hover:bg-[#2E3835] hover:text-white"
              >
                <HardDrive className="ml-3 h-3 w-3" />
                <span
                  className={`flex flex-1 items-center line-clamp-1 text-left truncate ${isDeviceSelected() ? 'font-bold text-white' : ''}`}
                >
                  {device.name}{' '}
                  {device.serialNumber ? (
                    <ExternalLink className="ml-1 h-3 w-3" />
                  ) : (
                    <></>
                  )}
                </span>
                <div className="flex items-center gap-1 font-semibold text-lime opacity-0 transition-opacity group-hover:opacity-100">
                  View
                </div>
              </button>
            </div>
          </div>
        );
      }

      case 'loading': {
        return (
          <div key={item.id} style={style}>
            <div className="flex items-center gap-2 px-2 py-2 text-sm text-white/50" style={{ marginLeft: `${item.level * 16}px` }}>
              <div className="h-3 w-3 animate-spin rounded-full border border-gray-600 border-t-transparent" />
              {item.message}
            </div>
          </div>
        );
      }

      case 'empty': {
        return (
          <div key={item.id} style={style}>
            <div className="flex line-clamp-1 items-center gap-2 px-2 py-2 text-sm text-white/40" style={{ marginLeft: `${item.level * 16 + 20}px` }}>
              {item.level === 1 ? (
                <Building2 className="h-3 w-3 text-gray-500" />
              ) : (
                <HardDrive className="h-3 w-3 text-gray-500" />
              )}
              {item.message}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  }, [
    virtualListItems,
    viewState,
    expandedCustomers,
    expandedSites,
    deviceCounts,
    loadingCounts,
    sitesLoaded,
    toggleCustomer,
    toggleSite,
    fetchDeviceCounts,
    setViewState,
    setAddSiteDialog,
    authStore,
  ]);

  // Debug logging
  console.log('Viewer2 render - Current viewState:', viewState);

  if (loading) {
    return (
      <div className="flex h-[calc(100vh_-_56px)] items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="grid grid-cols-[400px_1fr] ">
      <ResizablePanel defaultSize={14} minSize={12} className="">
        <section className="sticky top-0 h-[calc(100vh_-_56px)] overflow-y-scroll  border-[#2E3835] bg-[#111]">
          <div data-onboarding="viewer-toolbar" className="sticky top-0 z-20 flex min-h-[57px] items-center border-b bg-[#111]">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
              className="h-full rounded-none border-none pl-10 text-base"
              isSearch
            />
            <div className="flex items-center">
              {/* Sort Order Toggle Button */}
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="flex h-[56px] w-[56px] items-center justify-center border-l hover:bg-[#2E3835]"
              >
                {sortOrder === 'asc' ? (
                  <SortAsc className="h-4 w-4 text-lime" />
                ) : (
                  <SortDesc className="h-4 w-4 text-lime" />
                )}
              </button>

              {/* Sort Criteria Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex h-[56px] w-[56px] items-center justify-center border-l hover:bg-[#2E3835]">
                    {sortBy === 'name' && <Folder className="h-4 w-4 text-lime" />}
                    {sortBy === 'sites' && <Building2 className="h-4 w-4 text-lime" />}
                    {sortBy === 'devices' && <HardDrive className="h-4 w-4 text-lime" />}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="border-[#2E3835] bg-[#111]">
                  <DropdownMenuItem
                    onClick={() => setSortBy('name')}
                    className="cursor-pointer text-white hover:bg-[#2E3835]"
                  >
                    <Folder className="mr-2 h-4 w-4" />
                    By Name
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSortBy('sites')}
                    className="cursor-pointer text-white hover:bg-[#2E3835]"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    By Sites
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSortBy('devices')}
                    className="cursor-pointer text-white hover:bg-[#2E3835]"
                  >
                    <HardDrive className="mr-2 h-4 w-4" />
                    By Devices
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem
                    onClick={() => setSortBy('devices')}
                    className="cursor-pointer text-white hover:bg-[#2E3835]"
                  >
                    <Store className="mr-2 h-4 w-4" />
                    By Dealer
                  </DropdownMenuItem> */}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* Virtualized List Container */}
          <div
            data-onboarding="viewer-hierarchy"
            ref={parentRef}
            className="h-full overflow-auto"
            style={{
              height: 'calc(100vh - 113px)', // Adjust for header height
            }}
          >
            <div
              style={{
                minHeight: `${virtualizer.getTotalSize()}px`,
                height: 'auto',
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => (
                renderVirtualItem(virtualItem)
              ))}
            </div>
          </div>
        </section>
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel>
        <section data-onboarding="viewer-detail" className="sticky top-0 h-[calc(100vh_-_56px)] overflow-y-scroll ">
        {viewState.type === 'empty' && <EmptyComponent />}
        {viewState.type === 'customer' && (
          <CustomerComponent
            customer={customers.find((c) => c.id === viewState.customerId)}
            onCustomerUpdate={updateCustomerInList}
            disableUrlParams={true}
          />
        )}
        {viewState.type === 'site' && (
          <Site
            siteId={viewState.siteId}
            customerId={viewState.customerId}
            onSiteUpdate={(updatedSite) => {
              if (viewState.customerId) {
                updateSiteInList(viewState.customerId, updatedSite);
              }
            }}
          />
        )}
        {viewState.type === 'device' && (
          <DevicePage
            deviceId={viewState.deviceId || ''}
            hideNavigation={true}
            // onSave={(savedDevice) => {
            //   // Extract device data from SaveDeviceForm and update list
            //   if (viewState.customerId && viewState.siteId && savedDevice.nvr) {
            //     updateDeviceInList(viewState.customerId, viewState.siteId, {
            //       id: savedDevice.nvr.externalId || '',
            //       name: savedDevice.nvr.name || '',
            //     });
            //   }
            // }}
          />
        )}
        {viewState.type === 'addDevice' && (
          <div className="h-full">
            <AddDevice
              customerId={viewState.customerId}
              siteId={viewState.siteId}
              hideNavigation={true}
              onDeviceAdded={() => {
                // Refresh the data and return to empty state
                fetchData();
                setViewState({ type: 'empty' });
              }}
            />
          </div>
        )}
      </section>
      </ResizablePanel>

      {/* Add Site Dialog */}
      <AddSite
        open={addSiteDialog.open}
        onOpenChange={(open) => setAddSiteDialog((prev) => ({ ...prev, open }))}
        customerId={addSiteDialog.customerId}
        customerName={addSiteDialog.customerName}
        onSubmit={(params) => {
          // Add the new site to the specific customer without full refetch
          if (params.siteId && params.siteName) {
            addSiteToCustomer(addSiteDialog.customerId, {
              id: params.siteId,
              name: params.siteName,
            });
          }
          setAddSiteDialog({ open: false, customerId: '', customerName: '' });
        }}
        noNavigate={true}
      />
    </ResizablePanelGroup>
  );
}

const EmptyComponent = () => {
  return (
    <section className="flex h-[calc(100vh_-_56px)] items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-semibold">Nothing Selected</h2>
        <p className="text-sm text-gray-400">
          Select a customer, Site, or device in the menu to the left to view or edit.
        </p>
      </div>
    </section>
  );
};
